/**
 * Composer slice of the English catalog — per-platform post settings that are
 * not TikTok's (those live in enTiktok.ts).
 *
 * Carved out for the reason enScheduling.ts records: en.ts sits against the
 * 500-line fileSize ceiling, and the four X keys were the lines that pushed it
 * to 504L. Spread back into `en` in en.ts, so `t()` and MessageKey are
 * unchanged.
 */
import { enPerformance } from './enPerformance';

export const enComposer = {
  ...enPerformance,
  'x.settingsTitle': 'X Settings',
  'x.allowUrlCost': 'Allow link cost',
  'x.allowUrlCostHint': 'X bills posts that carry a link. Leave this off and a post with a link is refused instead of billed.',
  // A scheduled post in the calendar's day drawer.
  'social.post.media': 'Post media',
  // Carousel thumbnail: how many more photos sit behind the one shown.
  'social.post.moreMedia': '+{count}',
  'social.post.mediaCount': '{count} media files',
  'social.post.delete': 'Delete post',
  'social.post.deleteConfirm': 'Delete this post? It will not publish.',
  'social.post.deleteYes': 'Yes, delete',
  'social.post.deleteKeep': 'Keep it',
  'social.post.deleting': 'Deleting…',
  'social.post.deleted': 'Post deleted.',
  'social.post.deleteFailed': 'Could not delete this post. Refresh the page and try again.',
  // The post preview in the day drawer: full reel / carousel, caption, triggers.
  'social.post.preview': 'Preview',
  'social.post.hidePreview': 'Hide preview',
  'social.post.slide': 'Slide {n} of {count}',
  'social.post.noCaption': 'No caption.',
  'social.post.triggers': 'Triggers on this post',
  'social.post.triggersLoading': 'Loading triggers…',
  'social.post.triggersNone': 'No comment or story-reply trigger acts on this post yet.',
  'social.post.triggersFailed': 'Could not load the triggers. Close the preview and open it again.',
  'social.post.triggerAllPosts': 'All posts',
  'social.post.triggerThisPost': 'This post',
  'social.post.triggerOff': 'Off',
  'social.post.triggerAnyComment': 'Any comment',
  'social.post.triggerKeywords': 'Comment has: {keywords}',
  'social.post.triggerStoryReply': 'Story reply',
  'social.post.triggerDm': 'Sends DM: {text}',
  'social.post.triggerDmNoText': 'Sends a DM',
  'social.post.triggerPublicReply': 'Replies publicly',
  // First WhatsApp message from the customer card.
  'contacts.whatsappStart': 'Send WhatsApp message',
  'contacts.whatsappStartHint': 'WhatsApp lets a business start a conversation only with an approved template.',
  'contacts.whatsappStartNeedsConsent': 'Record WhatsApp consent above before sending the first message.',
  'contacts.whatsappStartSent': 'WhatsApp message sent. The conversation is in your inbox.',
  'contacts.whatsappStartFailed': 'The WhatsApp message was not sent. Check the template and the customer’s number, then try again.',
  'contacts.whatsappStartTemplatesFailed': 'Could not load your WhatsApp templates. Check that WhatsApp is connected, then try again.',
  // Flow canvas: the WhatsApp step.
  'flows.wa.template': 'Approved template',
  'flows.wa.noTemplate': 'No template — send a text message',
  'flows.wa.templateHint': 'A template is the only way WhatsApp lets you message someone first, such as a new lead from a landing page.',
  'flows.wa.noApproved': 'No approved WhatsApp templates yet. Create one in Settings → WhatsApp and wait for Meta to approve it.',
  'flows.wa.templatesFailed': 'Could not load your WhatsApp templates. Check that WhatsApp is connected, then reopen this step.',
  'flows.wa.param': 'Value {n}',
  'flows.wa.message': 'Message',
  'flows.wa.messageHint': 'A text message is delivered only within 24 hours of the customer’s last message. To message a new lead, choose a template.',
  // Draft → scheduled from the calendar.
  'social.post.draft': 'Draft',
  'social.post.activate': 'Activate',
  'social.post.activating': 'Activating…',
  'social.post.activateFailed': 'Could not activate this post. Open it in the Composer, choose the account to post from, and schedule it.',
  // Which connected accounts a post goes out from (AccountPicker).
  'social.accounts.postFrom': 'Post from',
  'social.accounts.chooseOne': 'Choose at least one {platform} account.',
  'social.accounts.chooseToSchedule': 'Choose which account to post from.',
  'social.accounts.one': '(1 account)',
  'social.accounts.many': '({count} accounts)',
  'x.linkDetected': 'This post contains a link. Turn on "Allow link cost" or remove the link, or X will refuse it.',
  'social.composer.heading': 'Composer',
  'social.composer.headingHint': 'Pick platforms, write your post, and schedule it.',
  'social.composer.platforms': 'Platforms',
  'social.composer.postTo': 'Post to',
  'social.composer.connected': 'Connected',
  'social.composer.notConnected': 'Not connected',
  'social.composer.notConnectedGroup': 'Not connected yet',
  'social.composer.connectLink': 'Connect in Integrations',
  'social.composer.noneConnected': 'No platforms connected yet.',
  'social.composer.noneConnectedHint': 'Connect a social account to start composing a post.',
  'social.composer.selectPlatform': 'Select a connected platform to continue.',
  'social.buzzpage.heading': 'Link in Bio',
  'social.composer.contentType': 'Content Type',
  'social.composer.youtubeVideo': 'Video',
  'social.composer.youtubeShorts': 'Shorts',
  'social.composer.tiktokVideo': 'Video',
  'social.composer.caption': 'Caption',
  'social.composer.captionLimit': '{count}/{max}',
  'social.composer.mediaDisabled': 'Remove a file to add more (max 10).',
  'social.composer.mediaSingleDisabled': 'Remove the file to upload another.',
  'social.composer.mediaUploadPercent': '{percent}%',
  'social.composer.mediaUploadProgress': 'Preparing {name}',
  'social.composer.mediaUploadFailed': 'Media upload failed. Try again.',
  'social.composer.mediaUploadNoUrl': 'Upload succeeded but no media URL was returned.',
  'social.composer.mediaPrepareFailed': 'Could not prepare this media. Try another file.',
  'social.composer.mediaUploadDismiss': 'Dismiss',
  'social.composer.readinessTitle': 'Publishing readiness',
  'social.composer.shortsGuide': 'Shorts: videos up to 60 seconds, ideally vertical (9:16).',
  'social.composer.timezone': 'Time zone',
  'social.composer.timezonePlaceholder': 'Select a time zone',
  'social.composer.detectedTimezone': 'Detected: {zone}',
  'social.composer.timezoneUseThis': 'Use this',
  'social.composer.timezoneDefaultHint':
    'Defaults to UTC conversion of your local time if left blank',
  'searchableMultiSelect.search': 'Search',
  'searchableMultiSelect.selectAll': 'Select all',
  'searchableMultiSelect.clear': 'Clear',
  'searchableMultiSelect.empty': 'No matches',
  'searchableMultiSelect.selected': '{count} selected',
  'searchableMultiSelect.remove': 'Remove {label}',
  'youtube.hashtags': 'Hashtags',
  'youtube.hashtagsPlaceholder': 'Type a hashtag and press Enter',
  'youtube.removeHashtag': 'Remove #{tag}',
  'youtube.titleOverride': 'Title override',
  'youtube.titleOverridePlaceholder': 'Optional YouTube title',
  'youtube.titleOverrideHint': 'Leave blank to use the first line of the caption.',
  'youtube.description': 'Description',
  'youtube.descriptionPlaceholder': 'Optional YouTube description',
  'youtube.descriptionHint': 'Leave blank to use the caption. Hashtags are added automatically.',
  'youtube.tags': 'Tags',
  'youtube.tagsPlaceholder': 'Comma-separated tags',
  'youtube.tagsHint': 'Search tags, separate from hashtags. Combined length up to 500 characters.',
  'youtube.category': 'Category',
  'youtube.license': 'License',
  'youtube.licenseYoutube': 'YouTube License',
  'youtube.licenseCreativeCommons': 'Creative Commons — Attribution',
  'youtube.embeddable': 'Embeddable',
  'youtube.publicStats': 'Public statistics viewable',
  'youtube.madeForKids': 'Made for kids',
  'youtube.syntheticMedia': 'Contains synthetic media',
  'youtube.paidPlacement': 'Paid product placement',
  'youtube.defaultLanguage': 'Default language',
  'youtube.defaultAudioLanguage': 'Default audio language',
  'youtube.languageDefault': 'Channel default',
  'youtube.allowedCountries': 'Allowed countries',
  'youtube.blockedCountries': 'Blocked countries',
  'youtube.countriesPlaceholder': 'Search countries',
  'youtube.countriesHint': 'YouTube accepts an allow list or a block list, not both. Country restrictions require a YouTube CMS/partner account; they are saved and sent when YouTube accepts them.',
  'youtube.thumbnailFile': 'Thumbnail file',
  'youtube.thumbnailUrl': 'Thumbnail URL',
  'youtube.thumbnailHint': 'JPG, PNG, GIF, or BMP. 1280 × 720 (16:9), 2 MB recommended.',
  'youtube.notifySubscribers': 'Notify subscribers',
  'youtube.notifyHint': 'Only applies to public uploads. Private and unlisted videos never notify. YouTube does not confirm whether a notification was delivered.',
  'youtube.firstComment': 'First comment',
  'youtube.firstCommentHint': 'Posted after the video is live (not pinned — YouTube’s API cannot pin). Private videos often cannot receive comments. Requires YouTube comment access — reconnect YouTube if this fails.',
  'youtube.firstCommentPlaceholder': 'Comment text',
  'youtube.publishNow': 'Publish Now',
  'youtube.publishing': 'Publishing…',
  'social.composer.publishNow': 'Publish Now',
  'social.composer.publishingNow': 'Publishing…',
  'social.composer.publishTikTokNow': 'Publish to TikTok now',
  'social.composer.publishing': 'Publishing...',
  'social.composer.saveTikTokDrafts': 'Save to TikTok drafts',
  'social.composer.saving': 'Saving…',
  'social.composer.saveDraft': 'Save Draft',
  'social.composer.schedule': 'Schedule',
  'social.composer.scheduleDate': 'Date',
  'social.composer.scheduleTime': 'Time',
  'social.composer.schedulePost': 'Schedule Post',
  'social.composer.scheduling': 'Scheduling…',
  'social.composer.preview': 'Preview',
  'social.composer.previewEmpty': 'Select an account to see how this post will look.',
  'social.composer.previewCaptionPlaceholder': 'Your caption appears here',
  'social.composer.previewMediaPlaceholder': 'Media appears here',
  'social.composer.previewJustNow': 'Just now',
  'social.composer.previewTab': 'Preview {name}',
  'social.composer.previewTabs': 'Account previews',
  'social.composer.previewPrevious': 'Previous media',
  'social.composer.previewNext': 'Next media',
  'social.composer.tiktokSettings': 'TikTok Settings',
  'social.composer.validation.contentRequired': 'Add a caption or media before scheduling.',
  'social.composer.validation.dateRequired': 'Choose a schedule date.',
  'social.composer.validation.datePast': 'Schedule date cannot be in the past.',
  'social.composer.validation.timeRequired': 'Choose a schedule time.',
  'social.composer.platformsSummary':
    'Posts to {count} platform{plural}{accounts} · {contentType}.',
  'social.composer.platformsSummaryNone': 'Posts to no platforms · {contentType}.',
  'social.replies.saved': 'Comment rule saved.',
  'social.replies.nameRequired': 'Enter a rule name.',
  'social.replies.keywordsRequired': 'Add at least one keyword, or reply to every comment.',
  'social.replies.dmRequired': 'Enter the DM message to send.',
  'social.buzzpage.linkLabelRequired': 'Enter a link label.',
  'social.buzzpage.linkUrlRequired': 'Enter an https:// address.',
  'social.buzzpage.linkUrlInvalid': 'Use a full https:// or http:// URL.',
  'social.buzzpage.titleRequired': 'Title cannot be empty.',
  'social.buzzpage.saved': 'Link in Bio page saved.',
  'social.buzzpage.loadError': 'Failed to load your page',
  'social.buzzpage.saveError': 'Could not save',
  'social.buzzpage.publicPage': 'Your public page',
  'social.buzzpage.open': 'Open',
  'social.buzzpage.copyLink': 'Copy link',
  'social.buzzpage.copied': 'Link copied',
  'social.buzzpage.copiedShort': 'Copied',
  'social.buzzpage.live': 'Live',
  'social.buzzpage.title': 'Title',
  'social.buzzpage.bio': 'Bio',
  'social.buzzpage.bioPlaceholder': 'One line about your business',
  'social.buzzpage.links': 'Links',
  'social.buzzpage.removeLink': 'Remove {label}',
  'social.buzzpage.emptyLinksTitle': 'No links yet',
  'social.buzzpage.emptyLinksBody': 'Add your booking page, event, or store so visitors have somewhere to go.',
  'social.buzzpage.emptyLinksCta': 'Add your first link',
  'social.buzzpage.labelPlaceholder': 'Label (e.g. Book a session)',
  'social.buzzpage.urlPlaceholder': 'https://…',
  'social.buzzpage.add': 'Add',
  'social.buzzpage.attributionHint':
    'Visitors who subscribe on your page become contacts with "came from: BuzzPage" attribution, and fire the Lead created trigger in your automations.',
  'social.buzzpage.previewAria': 'Live preview of your public page',
  'social.buzzpage.previewLabel': 'Preview',
  'social.buzzpage.previewOffline': 'Not live — visitors cannot see this page yet',
  'social.buzzpage.previewUntitled': 'Your title',
  'social.buzzpage.previewBioHint': 'Bio appears here',
  'social.buzzpage.previewNoLinks': 'Links show up here',
  'social.buzzpage.previewSubscribe': 'Stay in touch',

  // Facebook / Instagram New Post platform settings
  'metaPlace.label': 'Location (optional)',
  'metaPlace.placeholder': 'Search locations... ',
  'metaPlace.searchPlaceholder': 'Type at least 2 characters',
  'metaPlace.typeToSearch': 'Type to search worldwide places',
  'metaPlace.searching': 'Searching…',
  'metaPlace.empty': 'No matching locations',
  'metaPlace.clear': 'Clear location',
  'metaPlace.searchFailed': 'Could not search locations. Try again.',
  'metaPlace.authDenied':
    'Meta refused location search. Reconnect Facebook under Settings → Integrations, or paste a Facebook Page ID.',
  'metaPlace.notConnected':
    'Connect Facebook (Meta) to search locations. Instagram Login alone cannot look up places.',
  'facebook.settingsTitle': 'Facebook Settings',
  'facebook.locationHint':
    'Optional. Pick a Page associated with a place. Worldwide Meta Pages with location data can be tagged.',
  'facebook.link': 'Link (optional)',
  'facebook.linkPlaceholder': 'https://…',
  'facebook.firstComment': 'First comment (optional)',
  'facebook.firstCommentPlaceholder': 'Comment text...',
  'facebook.firstCommentHint': 'Posted after the content is live. Skipped for Stories (no comments edge).',
  'facebook.shareToStory': 'Also share to Story',
  'instagram.settingsTitle': 'Instagram Settings',
  'instagram.locationHint':
    'Optional. Only eligible Meta locations can be tagged. Unsupported results will be skipped at publish time.',
  'instagram.firstComment': 'First comment (optional)',
  'instagram.firstCommentPlaceholder': 'Comment text...',
  'instagram.collaborator': 'Invite collaborator',
  'instagram.collaboratorPlaceholder': '@username',
  'instagram.collaboratorHint': 'One Instagram username. They must accept the invite in the Instagram app.',
  'instagram.shareToStory': 'Also share to Story',
  'instagram.advancedTitle': 'Advanced Settings',
  'instagram.altText': 'Alt text',
  'instagram.altTextPlaceholder': 'Describe the image for accessibility',
  'instagram.altTextHint': 'Images only. Not applied to Reels or Stories.',
  'instagram.hideLikeCounts': 'Hide like and view counts',
  'instagram.hideLikeCountsUnavailable':
    'Unavailable — not available through Instagram’s publishing API for scheduled posts.',
  'instagram.disableComments': 'Turn off commenting',
} as const;
